/**
 * Every sentence the invoice-detail page (INV-02, `/stablecoins/invoices/view`)
 * renders, in one module — the pack rule that a page group keeps its English
 * copy in `src/features/invoicing/<page-name>/copy.ts`.
 *
 * Two things live here besides the strings:
 *
 *   1. **Refusal mapping.** Every write in this contract can be refused with a
 *      stable code from the LLD section 12 registry. `refusalMessage` turns a
 *      code this page can meet into a sentence a person can act on. A code the
 *      registry does not name (a service that ships a new code before this
 *      console does) is never given an invented meaning and never swallowed: it
 *      is shown verbatim, bounded to a fixed length, as text — `refusalDetail`
 *      below is the only path that prints a raw code, and React escapes it.
 *   2. **Error classification.** `classifyReadFailure` decides which of the
 *      page's four failure surfaces a read lands on. The critical rule is that
 *      a 404 and a cross-organisation 404 are the SAME classification: the
 *      service answers both with an identical `NOT_FOUND`, and the page must not
 *      be an existence oracle for ids the organisation does not hold.
 */

import type { ApiErrorInfo } from "@/lib/api";

/* --------------------------------------------------------------- strings */

export const DETAIL_COPY = {
  exportReceivablesLabel: "Export receivables",
  title: "Invoice",
  subtitle:
    "One invoice: both status axes, the pay link, every payment, the exceptions that need a decision, and the journal behind them.",

  /* page-level surfaces */
  noIdTitle: "No invoice selected",
  noIdDescription:
    "This page reads one invoice from its id, so nothing is requested until an id is present. Open an invoice from the receivables list to see it here.",
  noIdReason: "The request carries no ?id= query parameter.",

  notFoundTitle: "Invoice not found",
  notFoundDescription:
    "No invoice this organisation can read has that id. It may have been deleted, or it may belong to another organisation — the service answers both the same way, so this page cannot tell them apart and does not try.",

  readUnavailableTitle: "The invoice could not be read",
  readUnavailableDescription:
    "The issuer contract's invoice, payment and journal routes did not answer with this invoice, so nothing is rendered rather than a partial or invented view.",
  readUnavailableReasonPrefix: "The service refused the read",

  retry: "Try again",

  /* header / status axes */
  axesHeading: "Status",
  documentAxisLabel: "Document",
  paymentAxisLabel: "Payment",
  axesNote:
    "Two independent axes: the document can be issued while payment is partially paid. The service derives each one and this page renders them separately.",
  paidLateFlag: "Marked paid late",
  expiresAtLabel: "Expires",
  dueDateLabel: "Due",
  issuedLabel: "Issued",
  issuedByLabel: "Issued by",
  voidedByLabel: "Voided by",
  voidReasonLabel: "Void reason",
  documentHashLabel: "Document hash",
  memoLabel: "Memo",
  payerLabel: "Payer",
  numberLabel: "Number",

  /* totals */
  totalsHeading: "Amounts",
  totalsTotalLabel: "Total",
  totalsReceivedLabel: "Received",
  totalsAppliedLabel: "Applied",
  totalsExcessLabel: "Excess",
  totalsBaseUnitsNote: "Amounts are formatted from the contract's base-unit strings; nothing is rounded.",

  /* pay link */
  payLinkHeading: "Pay link",
  payLinkIdLabel: "Link id",
  payLinkNone: "No pay link has been minted for this invoice yet.",
  payLinkIdNote:
    "The link id is all the service re-serves. The link text itself is returned once, when it is minted, and is never readable again.",
  rotateCta: "Rotate pay link",
  rotateHint: "Revokes the current token and mints a new one. The old link stops working immediately.",
  rotateTitle: "Rotate the pay link?",
  rotateBody:
    "The current token is revoked as soon as the new one is minted. Anyone holding the old link will see the neutral not-active page.",
  rotateConfirm: "Rotate",
  rotateCancel: "Cancel",
  rotateShownOnceTitle: "New pay link — shown once",
  rotateShownOnceBody:
    "Copy it now and hand it to the payer. The service will not return it again, and this page forgets it when you leave.",
  copyCta: "Copy",
  copiedLabel: "Copied",
  copyFailedLabel: "Copy failed",
  payUrlLabel: "Pay URL",
  rotateReceipt: (createdAt: string) => `The service minted a new link at ${createdAt}.`,
  rotateMachineryGap:
    "A pay link is not readable after it is minted, so there is no control to copy the current link: rotating is the only way to obtain one from this page.",

  /* payments */
  paymentsHeading: "Payments",
  paymentsEmptyTitle: "No payments detected",
  paymentsEmptyDescription:
    "The service reported this invoice's payment list with no rows. Nothing has been detected against its addresses.",
  paymentsUnavailableTitle: "Payments could not be read",
  paymentsUnavailableDescription:
    "The payment list did not answer, so no payment is shown. An empty list here would be a claim the service never made.",
  paymentNetwork: "Network",
  paymentAmount: "Amount",
  paymentTxHash: "Transaction",
  paymentState: "State",
  paymentDetectedAt: "Detected",
  paymentFinalAt: "Final",
  paymentAppliedAt: "Applied",
  paymentAppliedAmount: "Applied amount",
  paymentExcessAmount: "Excess",
  paymentUnappliedReason: "Unapplied reason",
  paymentCaseRef: "Case",
  paymentJournalEntry: "Journal entry",
  paymentSourceAddress: "From",
  paymentFinalityNote:
    "Finality is reported as the payment's state and its final timestamp; this contract carries no confirmation count, so this table shows none.",

  /* exceptions */
  exceptionsHeading: "Exceptions",
  exceptionsEmptyTitle: "No exceptions",
  exceptionsEmptyDescription:
    "The service reported this invoice's exceptions with no rows, so no payment needs a resolution.",
  exceptionsUnavailableTitle: "Exceptions could not be read",
  exceptionsUnavailableDescription:
    "The exceptions list did not answer, so no exception is shown. Nothing is claimed either way.",
  exceptionType: "Type",
  exceptionAmount: "Amount",
  exceptionState: "State",
  exceptionCreatedAt: "Raised",
  exceptionReconRef: "Reconciliation",
  proposeResolutionCta: "Propose a resolution",
  proposeRefundCta: "Propose a refund",
  proposeResolutionForException: (exceptionId: string) =>
    `Propose a disposition for ${exceptionId}.`,
  proposalsOpenNote:
    "A resolution is a proposal, not a decision: it moves no money until a different person approves it.",
  approveLivesElsewhere:
    "Approving or rejecting a resolution happens on the resolution approvals page, under the approver's own permission and a fresh step-up. This page never offers it.",
  approvalLinkLabel: "Open resolution approvals",
  exceptionApprovalPending: "A resolution is already proposed for this exception.",

  /* propose form */
  proposeTitleResolve: "Propose a resolution",
  proposeTitleRefund: "Propose a refund",
  proposeKindLabel: "Disposition",
  proposeKindApply: "Apply to another open invoice",
  proposeKindApplyDescription: "Move the amount onto a second open invoice of the same payer.",
  proposeKindKeep: "Keep as payer credit",
  proposeKindKeepDescription: "Hold the amount as credit against the payer's future invoices.",
  proposeKindWriteOff: "Write off",
  proposeKindWriteOffDescription: "Give up the amount and close it out, recorded with a reason.",
  proposeKindRefund: "Refund the payer",
  proposeKindRefundDescription:
    "Send the amount back. The payer confirms the destination address out of band, and nothing is broadcast before screening.",
  proposeAmountLabel: "Amount",
  proposeAmountHint: (symbol: string, decimals: number) =>
    `In ${symbol}. An amount with more than ${decimals} decimal place${decimals === 1 ? "" : "s"} is refused rather than rounded.`,
  proposeTargetInvoiceLabel: "Target invoice id",
  proposeTargetInvoiceHint: "The open invoice of the same payer the amount should be applied to.",
  proposeReasonLabel: "Argument",
  proposeReasonHint: "Recorded with the proposal and shown to the approver as the evidence for it.",
  proposeReasonRequired: "A reason is required: the approver decides on it.",
  proposeTargetRequired: "An invoice id is required when applying to another invoice.",
  proposeSubmit: "Propose",
  proposeCancel: "Cancel",
  proposeUnavailableTitle: "The resolution could not be proposed",
  proposeSubmitted: (resolutionId: string, kind: string) =>
    `The service recorded resolution ${resolutionId} as a ${kind} proposal, pending a different approver.`,

  /* document actions */
  actionsHeading: "Actions",
  voidCta: "Void invoice",
  voidHint:
    "Withdraw the document, close its pay link and record why. A payment in flight refuses the void.",
  voidTitle: "Void this invoice?",
  voidBody:
    "The document moves to void and its pay link stops working. The reason is recorded and is not editable afterwards.",
  voidReasonFieldLabel: "Reason for voiding",
  voidReasonHint: "Required. Recorded on the invoice and on the journal.",
  voidReasonRequired: "A reason is required for the void to be accepted.",
  voidConfirm: "Void invoice",
  voidCancel: "Cancel",
  voidReceipt: (status: string) => `The service returned the invoice as ${status}.`,
  voidRefusedTitle: "The void was refused",
  voidRefusedInFlight:
    "A payment is in flight on this invoice, so the document cannot be voided. The payment is still being watched; void becomes available once it reaches a state the service accepts.",
  voidRefusedInFlightCode: "PAYMENT_IN_FLIGHT",

  duplicateCta: "Duplicate as a new draft",
  duplicateHint:
    "Copies the lines, asset and due terms into a fresh draft. Nothing is issued and no pay link is minted.",
  duplicateReceipt: (invoiceId: string) => `The service created draft ${invoiceId}.`,
  duplicateOpenLabel: "Open the new draft",

  remindCta: "Remind the payer",
  remindHint:
    "Sends the reminder to the payer's declared contact email. It never returns the pay link.",
  remindReceipt: (acceptedAt: string, channel: string) =>
    `The service accepted the ${channel} reminder at ${acceptedAt}. Acceptance is not delivery.`,

  /* journal */
  journalHeading: "Journal",
  journalEmptyTitle: "No journal entries",
  journalEmptyDescription:
    "The service reported this invoice's journal with no entries. Nothing has been posted against it.",
  journalUnavailableTitle: "The journal could not be read",
  journalUnavailableDescription:
    "The journal did not answer, so no entry is shown. The audit trail is never reconstructed from anything else.",
  journalEntryKind: "Entry",
  journalEntryCreatedAt: "Recorded",
  journalEntryLines: "Postings",
  journalEntryLinks: "References",
  journalEntryFlags: "Flags",
  journalPendingPrice: "pending price",
  journalDepeg: "price deviation",
  journalReverses: (entryId: string) => `reverses ${entryId}`,
  journalAmountsInBaseUnits: "Debit and credit are shown exactly as the service stores them: base units.",
  journalActorNote:
    "A journal entry carries no actor field, so the document's own issued-by and voided-by fields are shown instead of guessing an actor per entry.",
  journalTxHashNote:
    "The transaction hash comes from the payment the entry names, joined by its payment id.",

  /* shared bits */
  refusedTitle: "The action was refused",
  emptyValue: "—",
  baseUnitsLabel: "base units",
} as const;

/* ------------------------------------------------------------- refusals */

/**
 * Sentences for the LLD section 12 codes this page's actions can meet. Only
 * codes the registry names appear here; anything else is shown verbatim by
 * `refusalDetail`, never given a meaning this console invented.
 */
const REFUSAL_MESSAGES: Record<string, string> = {
  PAYMENT_IN_FLIGHT: DETAIL_COPY.voidRefusedInFlight,
  DOCUMENT_STATUS_INVALID:
    "The invoice is not in a document status this action is allowed from. Reload to see its current status.",
  DOCUMENT_NOT_DRAFT: "This action needs a draft, and this invoice is no longer a draft.",
  VERSION_CONFLICT:
    "The invoice changed after this page read it. Reload before trying again — nothing was written.",
  DUPLICATE_NOT_ALLOWED: "A draft cannot be duplicated. Issue it first, or create a new invoice.",
  NOTHING_DUE: "There is nothing due on this invoice, so no reminder was sent.",
  INVOICING_NOT_ENABLED: "Invoicing is not enabled for this organisation.",
  KYB_NOT_APPROVED: "Invoicing needs an approved KYB record before this action is allowed.",
  COLLECTION_WALLET_MISSING:
    "No validated collection wallet is configured for this invoice's network and asset.",
  ASSET_DECIMALS_MISMATCH:
    "The asset's decimals do not match the collection wallet's configuration, so the amount cannot be applied as written.",
  AMOUNT_PRECISION: "The amount has more decimal places than the asset supports.",
  INVALID_AMOUNT: "The amount is not a value this asset can carry.",
  DUE_DATE_PAST: "The due date has already passed.",
  PAYER_ARCHIVED: "This payer is archived, so a new invoice cannot be created for it.",
  ASSET_NOT_ENABLED: "That asset is not enabled for invoicing.",
  RESOLUTION_ALREADY_OPEN: "A resolution is already open for this exception.",
  APPLY_EXCEEDS_REMAINING: "The amount to apply is larger than what the target invoice still owes.",
  APPROVER_MUST_DIFFER_FROM_PROPOSER:
    "The approver must be a different person from the proposer, so this cannot be approved here.",
  MACHINE_PRINCIPAL_NOT_AN_APPROVER: "A machine principal can never approve a resolution.",
  RESOLUTION_NOT_PENDING: "This resolution is no longer pending, so no decision can be recorded.",
  AUTH_STEP_UP_REQUIRED:
    "This action needs a fresh step-up. Complete the step-up prompt yourself — this page does not retry it for you.",
  REFUND_STATE_INVALID: "This refund is not in a state that accepts a destination address.",
  ADDRESS_INVALID: "The address is not a valid, checksummed address for that network.",
  IDEMPOTENCY_KEY_REUSED:
    "The service saw this key before with a different request, so nothing was changed.",
  VALIDATION_FAILED: "The service rejected the request as invalid. Nothing was changed.",
  DEPENDENCY_UNAVAILABLE:
    "A service this action depends on is unavailable. Nothing was changed; the action can be retried.",
  FORBIDDEN: "This principal is not permitted to perform that action.",
  NOT_FOUND: "The service does not hold that record for this organisation.",
  NETWORK_ERROR:
    "The console could not reach the service, so it does not know whether anything happened. The key is reused if you retry, so a retry cannot double-post.",
  MALFORMED_RESPONSE:
    "The service answered with a body this console cannot read, so the outcome is unproven.",
};

/** A refusal code is printed verbatim only when it is unmapped, and always bounded. */
const MAX_CODE_LENGTH = 80;

/** The code as a bounded string: trimmed and cut, never rendered as markup. */
export function boundedCode(code: string | null | undefined): string {
  const trimmed = (code ?? "").trim();
  return trimmed.slice(0, MAX_CODE_LENGTH);
}

/**
 * A sentence for a refusal code. A registry code gets its sentence; anything
 * else is reported as an unmapped refusal carrying the code verbatim, so a code
 * this console has not learned yet is visible rather than silently dropped.
 */
export function refusalMessage(code: string | null | undefined): string {
  const key = boundedCode(code);
  if (!key) return "The service refused the action without a code.";
  return REFUSAL_MESSAGES[key] ?? `The service refused this action with ${key}.`;
}

/** True when the code is one the LLD section 12 registry names for this page. */
export function isMappedRefusal(code: string | null | undefined): boolean {
  return Object.prototype.hasOwnProperty.call(REFUSAL_MESSAGES, boundedCode(code));
}

/* ------------------------------------------------------ error classification */

export type ReadFailureKind = "not-found" | "denied" | "unavailable";

const NOT_FOUND_CODES = new Set(["NOT_FOUND", "HTTP_404"]);

/**
 * The codes a 403 arrives as. `responseSupport`/the invoicing client read the
 * body's `code` field, which for an authorisation refusal is `FORBIDDEN`; the
 * `AUTH_*` spellings are listed because the gateway's bounded-auth filter
 * renders those instead, and either spelling must reach the denied surface
 * rather than the generic one.
 */
const DENIED_CODES = new Set([
  "FORBIDDEN",
  "HTTP_403",
  "AUTH_POLICY_DENIED",
  "AUTH_INSUFFICIENT_SCOPE",
  "AUTH_RESOURCE_OWNERSHIP_MISMATCH",
]);

/**
 * Which failure surface a read belongs on.
 *
 * A 404 and a cross-organisation 404 are the same kind on purpose: the service
 * answers a missing id and an id the organisation does not hold with one
 * identical `NOT_FOUND` body, and this page must not become an existence oracle
 * by rendering them differently.
 */
export function classifyReadFailure(error: ApiErrorInfo): ReadFailureKind {
  const code = boundedCode(error.reasonCode);
  if (NOT_FOUND_CODES.has(code)) return "not-found";
  if (DENIED_CODES.has(code)) return "denied";
  return "unavailable";
}
